import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { AlertTriangle, File, Folder } from 'lucide-react'
import { useTheme, getThemeClass } from './ThemeProvider'
import { useTranslation } from '@papan/i18n'

/** What the person picked. `replace` is Replace — on every path a new VERSION of the existing file
 *  (same file, its history kept; owner, 2026-10-06) — and, for a folder, Merge. There is no
 *  user-facing "Add Version": it is what Replace does. */
export type ConflictResolution = 'replace' | 'skip' | 'keepBoth'

export type ConflictOperation = 'copy' | 'move' | 'upload' | 'extract'

export interface ConflictInfo {
  fileName: string
  sourcePath: string
  destPath: string
  isFolder: boolean
  /** Operation type determines which resolution options are shown */
  operation: ConflictOperation
  /** Whether to show the "Apply to all" checkbox (default: true) */
  showApplyToAll?: boolean
  /** The EXISTING item is a `.chain` / `.shared` link (says so; Replace can't write into it). */
  isPointer?: boolean
  /** Offer Replace (Merge for a folder)? Default true. False where nothing can be written into what
   *  is there — e.g. Save As onto a folder or a link: only Skip / Keep Both. */
  replaceable?: boolean
}

export interface ConflictResult {
  resolution: ConflictResolution
  applyToAll: boolean
}

interface ConflictContextValue {
  resolveConflict: (conflict: ConflictInfo) => Promise<ConflictResult>
}

const ConflictContext = createContext<ConflictContextValue | null>(null)

export function useConflict() {
  const context = useContext(ConflictContext)
  if (!context) {
    throw new Error('useConflict must be used within a ConflictProvider')
  }
  return context.resolveConflict
}

interface ModalState extends ConflictInfo {
  resolve: (result: ConflictResult) => void
}

export function ConflictProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalState | null>(null)
  const [applyToAll, setApplyToAll] = useState(false)

  const resolveConflict = useCallback((conflict: ConflictInfo): Promise<ConflictResult> => {
    return new Promise((resolve) => {
      setApplyToAll(false)
      setModal({ ...conflict, resolve })
    })
  }, [])

  const handleResolve = (resolution: ConflictResolution) => {
    modal?.resolve({ resolution, applyToAll })
    setModal(null)
  }

  const handleCancel = () => {
    // Treat cancel as skip
    modal?.resolve({ resolution: 'skip', applyToAll: false })
    setModal(null)
  }

  return (
    <ConflictContext.Provider value={{ resolveConflict }}>
      {children}
      {modal && (
        <ConflictModal
          {...modal}
          applyToAll={applyToAll}
          onApplyToAllChange={setApplyToAll}
          onResolve={handleResolve}
          onCancel={handleCancel}
        />
      )}
    </ConflictContext.Provider>
  )
}

interface ConflictModalProps extends ConflictInfo {
  applyToAll: boolean
  onApplyToAllChange: (value: boolean) => void
  onResolve: (resolution: ConflictResolution) => void
  onCancel: () => void
}

function ConflictModal({
  fileName,
  sourcePath,
  isFolder,
  isPointer = false,
  replaceable = true,
  showApplyToAll = true,
  applyToAll,
  onApplyToAllChange,
  onResolve,
  onCancel,
}: ConflictModalProps) {
  // Show folder path if the file is inside a subdirectory
  const folderPath = sourcePath && sourcePath !== fileName
    ? sourcePath.slice(0, sourcePath.lastIndexOf('/'))
    : null
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { t } = useTranslation('core-ui')

  // A file: Skip / Keep Both / Replace on every operation — Replace writes the incoming file as a
  // new VERSION of the existing one (nothing is binned; the old content stays in its history). A
  // folder: Skip / Keep Both / Merge.

  return (
    // z-[400], as ConfirmModal: the conflict prompt is awaited FROM another dialog (Save As, the
    // destination pickers — all z-50 or higher, portalled later in <body>), so at z-50 it rendered
    // BEHIND the very dialog waiting for its answer and that dialog's Save spun forever.
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 animate-fade-in"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full animate-scale-in ${themeClass} themed-bg-secondary`}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold themed-text mb-2">
                {isPointer ? t('conflict.linkTitle') : isFolder ? t('conflict.folderTitle', t('conflict.title')) : t('conflict.title')}
              </h3>
              <div className="flex items-center gap-2 mb-1">
                {isFolder ? (
                  <Folder size={20} className="text-yellow-500 shrink-0" />
                ) : (
                  <File size={20} className="text-zinc-400 shrink-0" />
                )}
                <span className="themed-text font-medium truncate">{fileName}</span>
              </div>
              {folderPath && (
                <p className="text-xs themed-text-muted mb-2 ml-7">in {folderPath}/</p>
              )}
              <p className="themed-text-secondary text-sm">
                {isPointer
                  ? t('conflict.linkMessage', { name: fileName })
                  : isFolder
                  ? t('conflict.folderMessage', { name: fileName, defaultValue: `A folder named "{{name}}" already exists in this location.` })
                  : t('conflict.message', { name: fileName })}
              </p>
            </div>
          </div>

          {/* Apply to all checkbox */}
          {showApplyToAll !== false && (
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="applyToAll"
                checked={applyToAll}
                onChange={(e) => onApplyToAllChange(e.target.checked)}
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-blue-500 focus:ring-blue-500"
              />
              <label htmlFor="applyToAll" className="themed-text-secondary text-sm cursor-pointer">
                {t('conflict.applyToAll')}
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={() => onResolve('skip')}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium transition-colors"
          >
            {t('conflict.skip')}
          </button>
          <button
            onClick={() => onResolve('keepBoth')}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium transition-colors"
          >
            {t('conflict.keepBoth')}
          </button>
          {replaceable && (
            <button
              onClick={() => onResolve('replace')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {isFolder ? t('conflict.merge', 'Merge') : t('conflict.replace')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
